import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    cards: {
      insertCards: (attrs?: { columns?: 1 | 2 | 3 | 4 }) => ReturnType;
      appendCardToCards: (cardsPos: number) => ReturnType;
    };
  }
}

function makeRandomId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function makeDefaultCard(index = 1) {
  return {
    type: 'card',
    attrs: {
      id: `c${makeRandomId()}`,
      cover: null,
      targetLink: null,
      fields: [
        {
          id: `f${makeRandomId()}`,
          type: 'text',
          label: 'Title',
          style: 'title',
          value: `Card ${index}`,
          visible: true,
        },
        {
          id: `f${makeRandomId()}`,
          type: 'text',
          label: 'Description',
          style: 'description',
          value: '',
          visible: true,
        },
      ],
    },
  };
}

export type CardsColumns = 1 | 2 | 3 | 4;

export interface CardsOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

export const Cards = Node.create<CardsOptions>({
  name: 'cards',
  group: 'block',
  content: 'card+',
  defining: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      columns: {
        default: 3,
        parseHTML: (el) => {
          const n = parseInt(el.getAttribute('data-columns') || '3', 10);
          if (n === 1 || n === 2 || n === 3 || n === 4) return n;
          return 3;
        },
        renderHTML: (attrs) => ({ 'data-columns': String(attrs.columns ?? 3) }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="cards"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addCommands() {
    return {
      insertCards:
        (attrs) =>
        ({ commands }) => {
          const columns = (attrs?.columns ?? 3) as CardsColumns;
          const initialCount = Math.max(
            2,
            Math.min(4, columns + (columns === 1 ? 1 : 0)),
          );
          const content = Array.from({ length: initialCount }, (_, i) =>
            makeDefaultCard(i + 1),
          );
          return commands.insertContent({
            type: this.name,
            attrs: { columns },
            content,
          });
        },
      appendCardToCards:
        (cardsPos) =>
        ({ state, dispatch, tr, editor }) => {
          const nodeAt = state.doc.nodeAt(cardsPos);
          if (!nodeAt || nodeAt.type.name !== 'cards') return false;
          const insertAt = cardsPos + nodeAt.nodeSize - 1;
          const count = nodeAt.childCount + 1;
          const cardJson = makeDefaultCard(count);
          const cardType = editor.schema.nodes['card'];
          if (!cardType) return false;
          const cardNode = cardType.createAndFill(cardJson.attrs);
          if (!cardNode) return false;
          if (dispatch) dispatch(tr.insert(insertAt, cardNode));
          return true;
        },
    };
  },

  addNodeView() {
    if (!this.options.view) return null;
    this.editor.isInitialized = true;
    return ReactNodeViewRenderer(this.options.view);
  },
});
